(() => {
  const drawer = document.getElementById('gift-wrap-drawer');
  const blocks = document.querySelectorAll('[data-gift-wrap-block]');

  if (!blocks.length) return;

  let packagingEnsuredGlobal = false;
  let packagingSyncInProgress = false;

  blocks.forEach((block) => {
    if (block.dataset.giftWrapInitialized === 'true') return;
    block.dataset.giftWrapInitialized = 'true';

    const productsScript = block.querySelector('[data-gift-wrap-products]');
    let products = [];

    try {
      products = JSON.parse(productsScript?.textContent || '[]');
    } catch (error) {
      products = [];
    }

    if (!Array.isArray(products) || products.length === 0) {
      block.hidden = true;
      return;
    }

    const runGlobalEnsure = !packagingEnsuredGlobal;
    if (runGlobalEnsure) packagingEnsuredGlobal = true;

    const inlineList = block.querySelector('[data-gift-wrap-products-list]');
    const isInline = Boolean(inlineList);

    const openButton = block.querySelector('[data-gift-wrap-open]');
    const editButton = block.querySelector('[data-gift-wrap-edit]');
    const selectedText = block.querySelector('[data-gift-wrap-selected]');

    const list = inlineList || drawer?.querySelector('[data-gift-wrap-products-list]');
    const addButton = isInline ? null : drawer?.querySelector('[data-gift-wrap-add-to-cart]');
    const backButton = drawer?.querySelector('[data-gift-wrap-back]');
    const closeButton = drawer?.querySelector('[data-gift-wrap-close]');
    const underlay = drawer?.querySelector('[data-gift-wrap-underlay]');

    const state = {
      selectedProductId: null,
      currentItem: null,
      isBusy: false,
      requiresPackaging: false,
    };

    let dedupeInProgress = false;

    const cartAddUrl = window.theme?.routes?.cart_add_url || '/cart/add.js';
    const cartChangeUrl = window.theme?.routes?.cart_change_url || '/cart/change.js';
    const cartUpdateUrl = window.theme?.routes?.cart_update_url || '/cart/update.js';
    const cartDataUrl = (window.theme?.routes?.cart_url || '/cart') + '.js';

    const productIdSet = new Set(products.map((product) => Number(product.id)));

    const formatText = (value) => (value || '').toString().trim();

    const getPreselectedProduct = () => products.find((product) => product.pre_select);

    const getPackagingItems = (cart) =>
      cart.items.filter((item) => productIdSet.has(Number(item.product_id)));

    const hasShippableProducts = (cart) =>
      (cart.items || []).some(
        (item) => item.requires_shipping === true && !productIdSet.has(Number(item.product_id))
      );

    const setPackagingUIVisible = (isVisible) => {
      block.hidden = !isVisible;
    };

    const setDrawerOpen = (isOpen) => {
      if (!drawer) return;
      if (isOpen) {
        drawer.classList.add('is-open');
        drawer.classList.remove('is-closing');
        drawer.setAttribute('aria-hidden', 'false');
        document.dispatchEvent(new CustomEvent('theme:scroll:lock', {bubbles: true}));
      } else {
        if (!drawer.classList.contains('is-open')) return;
        drawer.classList.add('is-closing');
        window.setTimeout(() => {
          drawer.classList.remove('is-open');
          drawer.classList.remove('is-closing');
          drawer.setAttribute('aria-hidden', 'true');
        }, 600);
        document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true}));
      }
    };

    const renderList = () => {
      if (!list) return;
      list.setAttribute('role', 'radiogroup');
      list.innerHTML = products
        .map((product) => {
          const isSelected = Number(product.id) === Number(state.selectedProductId);
          const imageMarkup = product.image
            ? `<img src="${product.image}" alt="${formatText(product.title)}" loading="lazy" />`
            : '';
          const priceDisplay = formatText(product.price) === '£0.00' ? 'Free' : formatText(product.price);

          return `
            <button
              type="button"
              class="gift-wrap__item${isSelected ? ' is-selected' : ''}"
              role="radio"
              aria-checked="${isSelected ? 'true' : 'false'}"
              data-gift-wrap-option
              data-product-id="${product.id}">
              <span class="gift-wrap__item__image">${imageMarkup}</span>
              <span class="gift-wrap__item__content">
                <span class="gift-wrap__item__title">${formatText(product.label || product.title)}</span>
                <span class="gift-wrap__item__price">${priceDisplay}</span>
              </span>
            </button>
          `;
        })
        .join('');
    };

    const updateAddButtonState = () => {
      if (!addButton) return;
      const hasSelection = state.selectedProductId !== null;
      addButton.disabled = state.isBusy || !hasSelection;
    };

    const updateSelectionState = () => {
      if (!list) return;
      const options = list.querySelectorAll('[data-gift-wrap-option]');
      options.forEach((option) => {
        const optionId = Number(option.dataset.productId);
        const isSelected = optionId === Number(state.selectedProductId);
        option.classList.toggle('is-selected', isSelected);
        option.setAttribute('aria-checked', isSelected ? 'true' : 'false');
      });

      updateAddButtonState();
    };

    const updateCompactUI = () => {
      if (selectedText) {
        if (state.currentItem) {
          const product = products.find((item) => Number(item.id) === Number(state.currentItem.product_id));
          selectedText.textContent = product ? formatText(product.label || product.title) : formatText(state.currentItem.product_title);
        } else {
          selectedText.textContent = '';
        }
      }

      if (!openButton || !editButton) return;

      if (state.currentItem) {
        editButton.classList.remove('is-hidden');
        openButton.classList.add('is-hidden');
      } else {
        editButton.classList.add('is-hidden');
        openButton.classList.remove('is-hidden');
      }
    };

    const fetchCart = async () => {
      const response = await fetch(cartDataUrl, {credentials: 'same-origin'});
      return response.json();
    };

    const removePackagingItem = async (lineKey) => {
      await fetch(cartChangeUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: lineKey,
          quantity: 0,
        }),
      });
    };

    const removeAllPackaging = async (exceptProductId = null) => {
      const cart = await fetchCart();
      const packagingItems = getPackagingItems(cart);

      for (const item of packagingItems) {
        if (exceptProductId !== null && Number(item.product_id) === Number(exceptProductId)) continue;
        await removePackagingItem(item.key);
      }
    };

    const dedupePackaging = async (cart, keepProductId = null) => {
      const packagingItems = getPackagingItems(cart);
      if (packagingItems.length <= 1) return false;

      let keepItem = packagingItems[0];
      if (keepProductId !== null) {
        const preferred = packagingItems.find((item) => Number(item.product_id) === Number(keepProductId));
        if (preferred) keepItem = preferred;
      }

      let changed = false;
      for (const item of packagingItems) {
        if (item.key === keepItem.key) continue;
        await removePackagingItem(item.key);
        changed = true;
      }

      return changed;
    };

    const addPackagingVariant = async (variantId) => {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', 1);

      await fetch(cartAddUrl, {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          Accept: 'application/javascript',
        },
        body: formData,
      });
    };

    const refreshCartSilently = () => {
      window.theme = window.theme || {};
      window.theme.suppressCartDrawerOpen = true;
      document.dispatchEvent(new CustomEvent('theme:cart:refresh', {bubbles: true}));
    };

    const updateCartNote = async (noteValue) => {
      await fetch(cartUpdateUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          note: noteValue,
        }),
      });
    };

    const applyPackagingState = (packagingItem) => {
      state.currentItem = packagingItem || null;
      state.selectedProductId = packagingItem ? Number(packagingItem.product_id) : null;
      updateCompactUI();
      renderList();
      updateSelectionState();
    };

    const syncPackagingForShippableCart = async () => {
      let cart = await fetchCart();
      const shippable = hasShippableProducts(cart);
      state.requiresPackaging = shippable;
      setPackagingUIVisible(shippable);

      if (!runGlobalEnsure) return false;

      let packagingItems = getPackagingItems(cart);
      let changed = false;

      if (!shippable) {
        if (packagingItems.length > 0) {
          for (const item of packagingItems) {
            await removePackagingItem(item.key);
          }
          changed = true;
        }
        return changed;
      }

      if (packagingItems.length > 1) {
        dedupeInProgress = true;
        changed = await dedupePackaging(cart, state.selectedProductId);
        dedupeInProgress = false;
        return changed;
      }

      if (packagingItems.length === 0) {
        const preselected = getPreselectedProduct();
        if (preselected?.primary_variant_id) {
          await addPackagingVariant(preselected.primary_variant_id);
          changed = true;
        }
      }

      return changed;
    };

    const syncStateFromCart = async () => {
      if (state.isBusy || dedupeInProgress || packagingSyncInProgress) return;

      try {
        let cart = await fetchCart();
        const shippable = hasShippableProducts(cart);
        state.requiresPackaging = shippable;
        setPackagingUIVisible(shippable);

        let packagingItems = getPackagingItems(cart);

        if (packagingItems.length > 1) {
          dedupeInProgress = true;
          const changed = await dedupePackaging(cart, state.selectedProductId);
          dedupeInProgress = false;

          if (changed) {
            document.dispatchEvent(new CustomEvent('theme:cart:refresh', {bubbles: true}));
            cart = await fetchCart();
            packagingItems = getPackagingItems(cart);
          }
        }

        applyPackagingState(shippable ? packagingItems[0] || null : null);
      } catch (error) {
        dedupeInProgress = false;
        console.error('Gift wrap: failed to sync cart', error);
      }
    };

    const handleCartChange = async () => {
      if (state.isBusy || dedupeInProgress || packagingSyncInProgress) return;

      if (runGlobalEnsure) {
        packagingSyncInProgress = true;
        setBusy(true);

        try {
          const changed = await syncPackagingForShippableCart();
          if (changed) {
            refreshCartSilently();
          }
        } catch (error) {
          console.error('Gift wrap: failed to sync packaging for shippable cart', error);
        } finally {
          packagingSyncInProgress = false;
          setBusy(false);
        }
      }

      await syncStateFromCart();
    };

    const setBusy = (isBusy) => {
      state.isBusy = isBusy;
      updateAddButtonState();
      addButton?.classList.toggle('is-loading', isBusy);
    };

    const handleAdd = async (shouldCloseDrawer = true) => {
      if (state.selectedProductId === null) return;

      const selectedProduct = products.find((product) => Number(product.id) === Number(state.selectedProductId));
      if (!selectedProduct || !selectedProduct.primary_variant_id) return;

      if (state.currentItem && Number(state.currentItem.product_id) === Number(state.selectedProductId)) {
        if (!isInline) setDrawerOpen(false);
        return;
      }

      setBusy(true);

      try {
        await removeAllPackaging();
        await addPackagingVariant(selectedProduct.primary_variant_id);
        document.dispatchEvent(new CustomEvent('theme:cart:refresh', {bubbles: true}));
        await syncStateFromCart();
        if (shouldCloseDrawer && !isInline) setDrawerOpen(false);
        const isCartPage = Boolean(block.closest('[data-section-type="cart"]'));
        if (selectedProduct.has_gift_note && !isCartPage) {
          document.dispatchEvent(new CustomEvent('theme:gift-note:open', {bubbles: true}));
        }
      } catch (error) {
        console.error('Gift wrap: failed to add packaging', error);
      } finally {
        setBusy(false);
      }
    };

    openButton?.addEventListener('click', () => {
      renderList();
      updateSelectionState();
      setDrawerOpen(true);
    });

    editButton?.addEventListener('click', () => {
      renderList();
      updateSelectionState();
      setDrawerOpen(true);
    });

    list?.addEventListener('click', (event) => {
      const option = event.target.closest('[data-gift-wrap-option]');
      if (!option) return;

      state.selectedProductId = Number(option.dataset.productId);
      updateSelectionState();

      if (isInline) {
        handleAdd(false);
      }
    });

    addButton?.addEventListener('click', () => handleAdd(true));
    backButton?.addEventListener('click', () => setDrawerOpen(false));
    closeButton?.addEventListener('click', () => setDrawerOpen(false));
    underlay?.addEventListener('click', () => setDrawerOpen(false));

    if (isInline) {
      const giftSection = block.closest('[data-section-type="cart-drawer"], [data-section-type="cart"]');
      const cartForm = giftSection?.querySelector('[data-cart-form]') || block.closest('[data-cart-form]');
      const checkoutButton = cartForm?.querySelector('button[name="checkout"]');
      const cartFormNoteField = cartForm?.querySelector('textarea[name="note"]');
      const cartFormNoteSaveButton = block.querySelector('[data-gift-note-inline-save]');
      const checkoutPopup = giftSection?.querySelector('[data-gift-checkout-popup]') || document.querySelector('[data-gift-checkout-popup]');
      const checkoutPopupClose = checkoutPopup?.querySelector('[data-gift-checkout-close]');
      const checkoutPopupSave = checkoutPopup?.querySelector('[data-gift-checkout-save]');
      const checkoutPopupList = checkoutPopup?.querySelector('[data-gift-checkout-products]');
      const checkoutPopupNote = checkoutPopup?.querySelector('[data-gift-checkout-note]');
      const checkoutPopupError = checkoutPopup?.querySelector('[data-gift-checkout-error]');
      let checkoutPopupSelectedProductId = null;
      let allowCheckoutOnce = false;

      const setInlineNoteButtonLabel = () => {
        if (!cartFormNoteSaveButton || !cartFormNoteField) return;

        const hasNote = cartFormNoteField.value.trim().length > 0;
        const addLabel = (cartFormNoteSaveButton.dataset.labelAdd || 'Add Note').trim();
        const updateLabel = (cartFormNoteSaveButton.dataset.labelUpdate || 'Update Note').trim();
        cartFormNoteSaveButton.textContent = hasNote ? updateLabel : addLabel;
      };

      const updateCheckoutPopupSelection = () => {
        if (!checkoutPopupList) return;
        checkoutPopupList.querySelectorAll('[data-gift-wrap-option]').forEach((option) => {
          const optionId = Number(option.dataset.productId);
          const isSelected = optionId === Number(checkoutPopupSelectedProductId);
          option.classList.toggle('is-selected', isSelected);
          option.setAttribute('aria-checked', isSelected ? 'true' : 'false');
        });
      };

      const renderCheckoutPopupList = () => {
        if (!checkoutPopupList) return;

        checkoutPopupList.setAttribute('role', 'radiogroup');
        checkoutPopupList.innerHTML = products
          .map((product) => {
            const isSelected = Number(product.id) === Number(checkoutPopupSelectedProductId);
            const imageMarkup = product.image
              ? `<img src="${product.image}" alt="${formatText(product.title)}" loading="lazy" />`
              : '';
            const priceDisplay = formatText(product.price) === '£0.00' ? 'Free' : formatText(product.price);

            return `
              <button
                type="button"
                class="gift-wrap__item${isSelected ? ' is-selected' : ''}"
                role="radio"
                aria-checked="${isSelected ? 'true' : 'false'}"
                data-gift-wrap-option
                data-product-id="${product.id}">
                <span class="gift-wrap__item__image">${imageMarkup}</span>
                <span class="gift-wrap__item__content">
                  <span class="gift-wrap__item__title">${formatText(product.label || product.title)}</span>
                  <span class="gift-wrap__item__price">${priceDisplay}</span>
                </span>
              </button>
            `;
          })
          .join('');
      };

      const setCheckoutPopupOpen = (isOpen) => {
        if (!checkoutPopup) return;
        checkoutPopup.classList.toggle('is-open', isOpen);
        checkoutPopup.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
      };

      const openCheckoutPopup = async () => {
        try {
          const cart = await fetchCart();
          const packagingItems = getPackagingItems(cart);
          checkoutPopupSelectedProductId = packagingItems[0] ? Number(packagingItems[0].product_id) : null;
          if (checkoutPopupNote) {
            const liveFormNoteValue = (cartFormNoteField?.value || '').toString();
            checkoutPopupNote.value = liveFormNoteValue !== '' ? liveFormNoteValue : (typeof cart.note === 'string' ? cart.note : '');
          }
          if (checkoutPopupError) checkoutPopupError.hidden = true;
          renderCheckoutPopupList();
          updateCheckoutPopupSelection();
          setCheckoutPopupOpen(true);
        } catch (error) {
          console.error('Gift wrap: failed to open checkout popup', error);
        }
      };

      const saveFromCheckoutPopup = async () => {
        if (!checkoutPopupSelectedProductId) {
          if (checkoutPopupError) checkoutPopupError.hidden = false;
          return;
        }

        const selectedProduct = products.find((product) => Number(product.id) === Number(checkoutPopupSelectedProductId));
        if (!selectedProduct || !selectedProduct.primary_variant_id) return;

        try {
          const cart = await fetchCart();
          const packagingItems = getPackagingItems(cart);
          const hasSelected =
            packagingItems.length === 1 &&
            Number(packagingItems[0].product_id) === Number(checkoutPopupSelectedProductId);

          if (!hasSelected) {
            await removeAllPackaging();
            await addPackagingVariant(selectedProduct.primary_variant_id);
          }

          const noteValue = (checkoutPopupNote?.value || '').toString();
          await updateCartNote(noteValue);
          if (cartFormNoteField) {
            cartFormNoteField.value = noteValue;
          }
          setInlineNoteButtonLabel();
          document.dispatchEvent(new CustomEvent('theme:cart:refresh', {bubbles: true}));
          await syncStateFromCart();

          setCheckoutPopupOpen(false);
          allowCheckoutOnce = true;
          if (checkoutButton) {
            if (typeof cartForm.requestSubmit === 'function') {
              cartForm.requestSubmit(checkoutButton);
            } else {
              checkoutButton.click();
            }
          }
        } catch (error) {
          console.error('Gift wrap: failed to save checkout popup data', error);
        }
      };

      checkoutPopupList?.addEventListener('click', (event) => {
        const option = event.target.closest('[data-gift-wrap-option]');
        if (!option) return;
        checkoutPopupSelectedProductId = Number(option.dataset.productId);
        if (checkoutPopupError) checkoutPopupError.hidden = true;
        updateCheckoutPopupSelection();
      });

      checkoutPopup?.querySelectorAll('[data-gift-note-open]').forEach((button) => {
        button.addEventListener('click', () => {
          setCheckoutPopupOpen(false);
        });
      });

      checkoutPopup?.addEventListener('click', (event) => {
        if (event.target === checkoutPopup) {
          setCheckoutPopupOpen(false);
        }
      });

      checkoutPopupClose?.addEventListener('click', () => setCheckoutPopupOpen(false));
      checkoutPopupSave?.addEventListener('click', saveFromCheckoutPopup);

      if (cartFormNoteField && cartFormNoteSaveButton) {
        setInlineNoteButtonLabel();

        cartFormNoteField.addEventListener('input', setInlineNoteButtonLabel);

        cartFormNoteSaveButton.addEventListener('click', async () => {
          const noteValue = cartFormNoteField.value.toString();
          cartFormNoteSaveButton.disabled = true;

          try {
            await updateCartNote(noteValue);
            document.dispatchEvent(new CustomEvent('theme:cart:refresh', {bubbles: true}));
            setInlineNoteButtonLabel();
          } catch (error) {
            console.error('Gift wrap: failed to save inline gift note', error);
          } finally {
            cartFormNoteSaveButton.disabled = false;
          }
        });
      }

      if (cartForm && cartForm.dataset.giftWrapCheckoutGuard !== 'true') {
        cartForm.dataset.giftWrapCheckoutGuard = 'true';

        cartForm.addEventListener('submit', (event) => {
          const submitter = event.submitter;
          const isCheckoutSubmit = submitter && submitter.name === 'checkout';

          if (!isCheckoutSubmit) return;

          if (allowCheckoutOnce) {
            allowCheckoutOnce = false;
            return;
          }

          if (!state.currentItem && state.requiresPackaging) {
            event.preventDefault();
            openCheckoutPopup();
          }
        });
      }
    }

    document.addEventListener('keyup', (event) => {
      if (event.key === 'Escape') {
        setDrawerOpen(false);
        const giftSection = block.closest('[data-section-type="cart-drawer"], [data-section-type="cart"]');
        const checkoutPopup = giftSection?.querySelector('[data-gift-checkout-popup]') || document.querySelector('[data-gift-checkout-popup]');
        checkoutPopup?.classList.remove('is-open');
        checkoutPopup?.setAttribute('aria-hidden', 'true');
      }
    });

    document.addEventListener('theme:cart-drawer:close', () => setDrawerOpen(false));
    document.addEventListener('theme:cart:change', () => handleCartChange());

    const initPackaging = async () => {
      if (runGlobalEnsure) {
        packagingSyncInProgress = true;
        setBusy(true);
        try {
          const changed = await syncPackagingForShippableCart();
          if (changed) {
            refreshCartSilently();
          }
        } catch (error) {
          console.error('Gift wrap: failed to sync packaging for shippable cart', error);
        } finally {
          packagingSyncInProgress = false;
          setBusy(false);
        }
      } else {
        try {
          const cart = await fetchCart();
          const shippable = hasShippableProducts(cart);
          state.requiresPackaging = shippable;
          setPackagingUIVisible(shippable);
        } catch (error) {
          console.error('Gift wrap: failed to evaluate shippable cart', error);
        }
      }

      await syncStateFromCart();
    };

    initPackaging();
  });
})();

(() => {
  const drawer = document.getElementById('gift-note-drawer');
  if (!drawer) return;
  if (drawer.dataset.giftNoteInitialized === 'true') return;
  drawer.dataset.giftNoteInitialized = 'true';

  const openButtons = document.querySelectorAll('[data-gift-note-open]');
  const closeButton = drawer.querySelector('[data-gift-note-close]');
  const underlay = drawer.querySelector('[data-gift-note-underlay]');
  const saveButton = drawer.querySelector('[data-gift-note-save]');
  const noteField = drawer.querySelector('[data-gift-note-field]');

  const cartUpdateUrl = window.theme?.routes?.cart_update_url || '/cart/update.js';
  const cartDataUrl = (window.theme?.routes?.cart_url || '/cart') + '.js';

  const setDrawerOpen = (isOpen) => {
    if (isOpen) {
      drawer.classList.add('is-open');
      drawer.classList.remove('is-closing');
      drawer.setAttribute('aria-hidden', 'false');
      document.dispatchEvent(new CustomEvent('theme:scroll:lock', {bubbles: true}));
    } else {
      if (!drawer.classList.contains('is-open')) return;
      drawer.classList.add('is-closing');
      window.setTimeout(() => {
        drawer.classList.remove('is-open');
        drawer.classList.remove('is-closing');
        drawer.setAttribute('aria-hidden', 'true');
      }, 600);
      document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true}));
    }
  };

  const fetchCartNote = async () => {
    const response = await fetch(cartDataUrl, {credentials: 'same-origin'});
    const cart = await response.json();
    if (noteField && typeof cart.note === 'string') {
      noteField.value = cart.note;
    }
  };

  const openGiftNoteDrawer = async () => {
    await fetchCartNote();
    setDrawerOpen(true);
    noteField?.focus();
  };

  const saveCartNote = async () => {
    const noteValue = (noteField?.value || '').toString();
    await fetch(cartUpdateUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        note: noteValue,
      }),
    });
  };

  openButtons.forEach((button) => {
    button.addEventListener('click', () => {
      openGiftNoteDrawer();
    });
  });

  document.addEventListener('theme:gift-note:open', () => {
    openGiftNoteDrawer();
  });

  saveButton?.addEventListener('click', async () => {
    await saveCartNote();
    document.dispatchEvent(new CustomEvent('theme:cart:refresh', {bubbles: true}));
    setDrawerOpen(false);
  });

  closeButton?.addEventListener('click', () => setDrawerOpen(false));
  underlay?.addEventListener('click', () => setDrawerOpen(false));

  document.addEventListener('keyup', (event) => {
    if (event.key === 'Escape') setDrawerOpen(false);
  });
})();
